import React, { useState, useEffect, useCallback, useMemo } from 'react';
import firebase from '../../../config/firebase.config';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import {
  addNotification,
  notificationStates,
} from '../../../components/Notification';
import { importQuickBooksEmployees } from '../../../services/quickbooks';
import { importSalesforceEmployees } from '../../../services/salesforce';
import { importSageHREmployees } from '../../../services/sagehr';
import { importZohoPeopleEmployees } from '../../../services/zohoPeople';
import styles from './IntegrationImportTab.module.scss';

const STATUS_LABELS = {
  imported: 'Created',
  invited: 'Invited',
  mapped: 'Linked',
  skipped: 'Skipped',
  error: 'Failed',
};

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'imported', label: 'Created' },
  { key: 'invited', label: 'Invited' },
  { key: 'mapped', label: 'Linked' },
  { key: 'skipped', label: 'Skipped' },
  { key: 'error', label: 'Failed' },
];

function fmtDate(d) {
  if (!d) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

export const IntegrationImportTab = ({ companyId, teamId, userId, source }) => {
  const [logMeta, setLogMeta] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [reimporting, setReimporting] = useState(false);

  const integrationDocId =
    source === 'quickbooks'
      ? 'quickbooksTime'
      : source === 'sagehr'
        ? 'sagehr'
        : source === 'zohopeople'
          ? 'zohoPeople'
          : 'salesforce';

  const sourceName =
    source === 'quickbooks'
      ? 'QuickBooks'
      : source === 'sagehr'
        ? 'Sage HR'
        : source === 'zohopeople'
          ? 'Zoho People'
          : 'Salesforce';

  useEffect(() => {
    if (!companyId) {
      setLogMeta(null);
      setEmployees([]);
      return;
    }

    setLoading(true);
    setLogMeta(null);
    setEmployees([]);

    const unsub = firebase
      .firestore()
      .collection('companyDatabase')
      .doc(companyId)
      .collection('integrations')
      .doc(integrationDocId)
      .collection('importLogs')
      .orderBy('importedAt', 'desc')
      .limit(1)
      .onSnapshot(
        async (snap) => {
          if (snap.empty) {
            setLogMeta(null);
            setEmployees([]);
            setLoading(false);
            return;
          }

          const d = snap.docs[0];
          const data = d.data();
          setLogMeta({
            id: d.id,
            importedAt: data.importedAt?.toDate?.() ?? null,
            importedBy: data.importedBy || {},
            status: data.status || 'completed',
            stats: data.stats || {},
          });

          try {
            const detailDoc = await firebase
              .firestore()
              .collection('companyDatabase')
              .doc(companyId)
              .collection('integrations')
              .doc(integrationDocId)
              .collection('importLogs')
              .doc(d.id)
              .collection('details')
              .doc('data')
              .get();
            setEmployees(
              detailDoc.exists ? detailDoc.data().employees || [] : []
            );
          } catch (err) {
            console.error('Failed to load import details', err);
            setEmployees([]);
          } finally {
            setLoading(false);
          }
        },
        (err) => {
          console.error('Import log subscription error', err);
          setLoading(false);
        }
      );

    return unsub;
  }, [companyId, integrationDocId]);

  const counts = useMemo(() => {
    const c = {
      all: employees.length,
      imported: 0,
      invited: 0,
      mapped: 0,
      skipped: 0,
      error: 0,
    };
    for (const e of employees) {
      if (c[e.outcome] !== undefined) c[e.outcome]++;
    }
    return c;
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    if (statusFilter === 'all') return employees;
    return employees.filter((e) => e.outcome === statusFilter);
  }, [employees, statusFilter]);

  const handleReimport = useCallback(async () => {
    if (!teamId) {
      addNotification({
        message: 'Select a department before re-importing.',
        level: notificationStates.error,
      });
      return;
    }
    setReimporting(true);
    try {
      const fn =
        source === 'quickbooks'
          ? importQuickBooksEmployees
          : source === 'sagehr'
            ? importSageHREmployees
            : source === 'zohopeople'
              ? importZohoPeopleEmployees
              : importSalesforceEmployees;
      const result = await fn(companyId, teamId, userId);
      const parts = [];
      if (result.imported > 0) parts.push(`${result.imported} created`);
      if (result.invited > 0) parts.push(`${result.invited} invited`);
      if (result.mapped > 0) parts.push(`${result.mapped} linked`);
      if (result.errors > 0) parts.push(`${result.errors} failed`);
      addNotification({
        message: `Import complete: ${parts.join(', ') || 'nothing new'}.`,
        level:
          result.errors > 0
            ? notificationStates.warning
            : notificationStates.success,
      });
    } catch {
      addNotification({
        message: `Re-import failed. Please try again.`,
        level: notificationStates.error,
      });
    } finally {
      setReimporting(false);
    }
  }, [companyId, teamId, userId, source]);

  if (!companyId) {
    return (
      <div className={styles.emptyState}>
        Select a company to view {sourceName} import data.
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.spinner} />
        Loading import data…
      </div>
    );
  }

  if (!logMeta) {
    return (
      <div className={styles.emptyState}>
        <span>No import runs found.</span>
        <span className={styles.emptyStateHint}>
          Run an import from the Integrations page to see data here.
        </span>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Last run info banner */}
      <div className={styles.runBanner}>
        <div className={styles.runBannerLeft}>
          <span className={styles.runDate}>
            Last import · {fmtDate(logMeta.importedAt)}
          </span>
          <span className={styles.runBy}>
            by{' '}
            {logMeta.importedBy?.name ||
              logMeta.importedBy?.userId ||
              'Unknown'}
          </span>
        </div>
        <div className={styles.runStats}>
          {(logMeta.stats?.imported || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipGreen}`}>
              {logMeta.stats.imported} created
            </span>
          )}
          {(logMeta.stats?.invited || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipBlue}`}>
              {logMeta.stats.invited} invited
            </span>
          )}
          {(logMeta.stats?.mapped || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipGray}`}>
              {logMeta.stats.mapped} linked
            </span>
          )}
          {(logMeta.stats?.skipped || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipGray}`}>
              {logMeta.stats.skipped} skipped
            </span>
          )}
          {(logMeta.stats?.errors || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipRed}`}>
              {logMeta.stats.errors} failed
            </span>
          )}
        </div>
        <Button
          text={reimporting ? 'Re-importing…' : `Re-import from ${sourceName}`}
          variant={buttonVariants.primary}
          size={buttonSizes.sm}
          onClick={handleReimport}
          disabled={reimporting || !teamId}
        />
      </div>

      {/* Status filter chips */}
      <div className={styles.filterRow}>
        {STATUS_FILTERS.map(({ key, label }) => {
          if (key !== 'all' && counts[key] === 0) return null;
          return (
            <button
              key={key}
              className={`${styles.filterChip} ${statusFilter === key ? styles.filterChipActive : ''}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
              <span className={styles.filterCount}>{counts[key]}</span>
            </button>
          );
        })}
      </div>

      {/* Employee list */}
      {filteredEmployees.length === 0 ? (
        <div className={styles.emptyState}>No employees match this filter.</div>
      ) : (
        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <span>Name</span>
            <span>Email</span>
            <span>{source === 'quickbooks' ? 'QB ID' : 'SF Contact ID'}</span>
            <span>Status</span>
          </div>
          {filteredEmployees.map((emp, idx) => (
            <div key={emp.externalId ?? idx} className={styles.tableRow}>
              <span className={styles.empName}>{emp.name || '—'}</span>
              <span className={styles.empEmail}>
                {emp.email || <span className={styles.noEmail}>No email</span>}
              </span>
              <span className={styles.empId}>{emp.externalId || '—'}</span>
              <span className={styles.empStatus}>
                <span
                  className={`${styles.statusTag} ${styles[`tag_${emp.outcome}`]}`}
                >
                  {STATUS_LABELS[emp.outcome] || emp.outcome}
                </span>
                {emp.outcome === 'error' && emp.reason && (
                  <span className={styles.errorReason}>{emp.reason}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
